import { Box, Container, Hidden, Typography } from "@material-ui/core";
import { graphql } from "gatsby";
import React, { useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { convertDatoCmsAssetUrlToCloudfrontUrl } from "../../helpers/cdn";
import { formatMultilinesText } from "../../helpers/markdown";
import BarChart from "./BarChart";
import { CustomHeading, CustomVideo } from "./styles";
import { DeviceFeaturesBarChartsFragment as Props } from "./__generated__/DeviceFeaturesBarChartsFragment";
import Imgix from "react-imgix";

export default function DeviceFeaturesBarChartsSection({
  text,
  secondaryText,
  desktopImage,
  mobileImage,
  showBarChart,
  barChart,
  videoSources,
  videoMode
}: Props) {
  const barChartData = barChart ? JSON.parse(barChart) : null;

  const hasConfiguredVideos = videoSources?.length > 0;
  const videoPoster = videoSources?.find(v => v.mimeType === "video/mp4")?.video
    .thumbnailUrl; // Use mp4 video has source for poster, else it will have a black background

  const videoRef = useRef<HTMLVideoElement>();
  const [inViewRef, inView] = useInView({
    threshold: 0.5,
    skip: videoMode === "loop" || !hasConfiguredVideos
  });

  /**
   * Default video for non-looping videos:
   * 1. Play video when entering the viewport
   * 2. Reset video after leaving the viewport
   * 3. Play video again when re-entering the viewport
   */
  useEffect(() => {
    if (!hasConfiguredVideos || !videoRef.current || videoMode === "loop") {
      return;
    }
    if (inView) {
      // Older browsers may not return a value from play()
      const promise = videoRef.current.play();
      if (promise) {
        promise.catch(e => {});
      }
    } else {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch (error) {}
    }
  }, [inView]);

  return (
    <Container maxWidth="md">
      <Box display="flex" flexDirection={{ xs: "row-reverse", sm: "row" }}>
        {/* Image/Video */}
        <Box
          width={{ xs: "148px", sm: "270px" }}
          marginRight={{ xs: 0, sm: 6 }}
          marginLeft={{ xs: 3, sm: 0 }}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          ref={inViewRef}
        >
          {/* Device video */}
          {hasConfiguredVideos && (
            <CustomVideo
              controls={false}
              preload="auto"
              loop={videoMode === "loop"}
              autoPlay={videoMode === "loop"}
              muted
              playsInline
              // poster={`${videoPoster}?time=0`}
              ref={videoRef}
              disableRemotePlayback={true}
            >
              {videoSources.map(videoSource => (
                <source
                  key={videoSource.url}
                  src={convertDatoCmsAssetUrlToCloudfrontUrl(videoSource.url)}
                  type={videoSource.mimeType}
                />
              ))}
            </CustomVideo>
          )}

          {/* Fallback to static image */}
          {!hasConfiguredVideos && (
            <>
              {/* Mobile */}
              <Hidden mdUp implementation="css">
                <Imgix
                  src={mobileImage.url}
                  width={148}
                  imgixParams={{
                    q: 75,
                    auto: "format,compress"
                  }}
                  htmlAttributes={{
                    loading: "lazy"
                  }}
                />
              </Hidden>
              {/* Desktop */}
              <Hidden smDown implementation="css">
                <Imgix
                  src={desktopImage.url}
                  width={320}
                  imgixParams={{
                    q: 75,
                    auto: "format,compress"
                  }}
                  htmlAttributes={{
                    loading: "lazy"
                  }}
                />
              </Hidden>
            </>
          )}
        </Box>

        {/* Text + Bar chart */}
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          justifyContent="center"
          marginLeft={{ xs: 1, sm: 0 }}
        >
          <CustomHeading
            variant="h4"
            dangerouslySetInnerHTML={{ __html: formatMultilinesText(text) }}
            className="white"
          />

          {secondaryText && (
            <Box mt={{ xs: 3, md: 6 }}>
              <Typography
                variant="h6"
                className="white"
                dangerouslySetInnerHTML={{
                  __html: formatMultilinesText(secondaryText)
                }}
              />
            </Box>
          )}

          {/* Bar Chart (Desktop) */}
          {showBarChart && barChartData && (
            <Hidden xsDown implementation="css">
              <Box mt={6}>
                <BarChart data={barChartData} />
              </Box>
            </Hidden>
          )}
        </Box>
      </Box>

      {/* Bar Chart (Mobile) */}
      {showBarChart && barChartData && (
        <Hidden smUp implementation="css">
          <BarChart data={barChartData} />
        </Hidden>
      )}
    </Container>
  );
}

export const DeviceFeaturesBarChartFragment = graphql`
  fragment DeviceFeaturesBarChartsFragment on DatoCmsCModuleDeviceFeaturesSection {
    text
    secondaryText
    desktopImage: asset {
      url
    }
    mobileImage: assetMobile {
      url
    }
    videoSources {
      mimeType
      url
      video {
        thumbnailUrl(format: png)
      }
    }
    videoMode
    showBarChart
    barChart
    container
    containerDisableGutters
    containerBackgroundColor
    model {
      apiKey
    }
  }
`;
